import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import DocumentLoading from './document-loading';
import DocumentError from './document-error';

const MAX_PREVIEW_SIZE = 5 * 1024 * 1024;

const STORAGE_SHIM = `<script>
(function () {
  function makeStorage() {
    var store = Object.create(null);
    return {
      getItem: function (k) { return k in store ? store[k] : null; },
      setItem: function (k, v) { store[k] = String(v); },
      removeItem: function (k) { delete store[k]; },
      clear: function () { store = Object.create(null); },
      key: function (i) { var ks = Object.keys(store); return i < ks.length ? ks[i] : null; },
      get length() { return Object.keys(store).length; }
    };
  }
  try { void window.localStorage; } catch (e) {
    Object.defineProperty(window, 'localStorage', { value: makeStorage(), configurable: true });
  }
  try { void window.sessionStorage; } catch (e) {
    Object.defineProperty(window, 'sessionStorage', { value: makeStorage(), configurable: true });
  }
  try { void document.cookie; } catch (e) {
    Object.defineProperty(document, 'cookie', { get: function () { return ''; }, set: function () {}, configurable: true });
  }
})();
<\/script>`;

function injectShim(html) {
  const head = html.match(/<head[^>]*>/i);
  if (head) return html.slice(0, head.index + head[0].length) + STORAGE_SHIM + html.slice(head.index + head[0].length);
  const htmlTag = html.match(/<html[^>]*>/i);
  if (htmlTag) return html.slice(0, htmlTag.index + htmlTag[0].length) + STORAGE_SHIM + html.slice(htmlTag.index + htmlTag[0].length);
  const doctype = html.match(/<!DOCTYPE[^>]*>/i);
  if (doctype) return html.slice(0, doctype.index + doctype[0].length) + STORAGE_SHIM + html.slice(doctype.index + doctype[0].length);
  return STORAGE_SHIM + html;
}

const HtmlPreview = memo(({ src, fileName }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!src) return;
    let createdUrl = null;
    setLoading(true);
    setError(null);

    fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load file');
        const contentLength = response.headers.get('content-length');
        if (contentLength && parseInt(contentLength) > MAX_PREVIEW_SIZE) {
          throw new Error('File too large to preview');
        }
        return response.text();
      })
      .then((text) => {
        if (text.length > MAX_PREVIEW_SIZE) {
          throw new Error('File too large to preview');
        }
        const blob = new Blob([injectShim(text)], { type: 'text/html; charset=utf-8' });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setLoading(false);
      })
      .catch((err) => {
        console.error('HTML preview error:', err);
        setError(err.message || 'Unable to load HTML file');
        setLoading(false);
      });

    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src]);

  if (loading) return <DocumentLoading />;
  if (error) return <DocumentError message={error} />;
  if (!blobUrl) return null;

  return (
    <div className="u_fv-html-preview">
      <iframe
        src={blobUrl}
        title={fileName || 'HTML Preview'}
        sandbox="allow-scripts allow-popups allow-modals allow-forms allow-presentation"
        referrerPolicy="no-referrer"
        width="100%"
        height="100%"
        frameBorder="0"
      />
    </div>
  );
});

HtmlPreview.propTypes = {
  src: PropTypes.string.isRequired,
  fileName: PropTypes.string,
};

export default HtmlPreview;
